import { graphHooks } from '@tunasong/graph-lib/react'
import type { FeedItem } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import { UserAvatar, getProfileColor, Avatar } from '@tunasong/ui-lib'
import { useContext, type FC, type Ref } from 'react'
import { AssistantAvatarContext, AssistantNameContext } from './assistant-avatar-context.js'
import ChatItem from './chat-item.js'
import { isAgentAuthored } from './is-agent-authored.js'
import styles from './chat-feed-item.module.css'

export interface ChatFeedItemProps {
  item: FeedItem
  highlight?: boolean
  ref: Ref<HTMLDivElement>
}

const userAvatarWithUser = (userId: string) => () => (
  <UserAvatar className={styles.avatar} userId={userId} size="tiny" />
)
const DummyAvatar = () => <Avatar className={styles.avatar} />

export const ChatFeedItem: FC<ChatFeedItemProps> = props => {
  const { item, highlight = false, ref, ...restProps } = props
  const itemUserId = item.userId
  const { userId: currentUserId } = useCurrentUser()

  // A proposal is the agent's even when its row is owned by the user who asked (GH #2451), so its side,
  // avatar and name are the assistant's too: a legacy proposal must not carry the user's name
  const isAssistant = isAgentAuthored(item)
  const { profile } = graphHooks.useProfile(isAssistant ? 'assistant' : item.userId)
  // Per-thread override for the assistant avatar. `AgentChat` (plugin-agent)
  // provides this so the agent's icon + color render for `userId === 'assistant'`
  // comments. plugin-chat cannot depend on plugin-agent (circular), so we
  // invert the dependency via context instead. Default `null` → fall back to
  // the generic DummyAvatar.
  const AssistantAvatar = useContext(AssistantAvatarContext)
  const resolveUserIcon = (): FC => {
    if (isAssistant && AssistantAvatar) {
      return AssistantAvatar
    }
    if (itemUserId && !isAssistant) {
      return userAvatarWithUser(itemUserId)
    }
    return DummyAvatar
  }

  const UserIcon = resolveUserIcon()
  const type = isAssistant ? 'assistant' : currentUserId === item.userId ? 'message' : 'response'
  const { colorName } = getProfileColor(profile)
  // In an agent's own thread, its display name labels its comments (a renamed "Nils", not "Tunabrain")
  const assistantName = useContext(AssistantNameContext)

  return (
    <ChatItem
      key={item.id}
      {...restProps}
      ref={ref}
      highlight={highlight}
      type={type}
      color={colorName}
      name={(type === 'assistant' ? assistantName : null) ?? profile?.name ?? 'Unknown'}
      actions={item.actions}
      UserIcon={UserIcon}
      timestamp={item.createdAt}
    >
      {item.content}
    </ChatItem>
  )
}

export default ChatFeedItem
